import { useEffect, useMemo, useState } from 'react'
import { Activity, Download, Signal as SignalIcon } from 'lucide-react'
import ClusterCard from './ClusterCard'
import { EmptyState } from '../ui/EmptyState'
import { SignalRowSkeleton } from '../ui/Skeleton'
import { ClusterSkeleton } from './Skeleton'
import { getSeverityClasses } from './severityStyles'
import { useActions, useCompetitors, useSignalClusters, useSignals } from '../../hooks/useCompetitiveData'
import { exportToCSV } from '../../lib/export-utils'
import { apiClient } from '../../lib/api-client'
import { humanizeActionFinding } from '../../lib/competitive-utils'
import { canonicalBrandName } from '../../lib/utils'
import type { SignalRow } from '../../types/competitive'

const RAW_SIGNALS_BATCH_SIZE = 25

type ViewMode = 'intelligence' | 'raw'
type SignalTimeGroup = 'Today' | 'Yesterday' | 'This Week' | 'Older'

interface SignalFeedTabProps {
  onNavigateToCompetitor?: (competitorName: string) => void
}

function formatTimestamp(value: string | null): string {
  if (!value) {
    return 'No updates yet'
  }

  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function normalizeNumericValue(value: number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null
  }
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return null
  }
  return parsed
}

function formatRank(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return 'Not available'
  }
  return `#${Math.round(value).toLocaleString('en-US')}`
}

function channelLabel(channel: string): string {
  if (channel === 'amazon') return 'Amazon'
  if (channel === 'tiktok') return 'TikTok'
  if (channel === 'google' || channel === 'google_shopping') return 'Google'
  return channel
}

function formatSignalDescription(signal: SignalRow): string {
  const channel = signal.signal_channel.toLowerCase()
  const anomalyType = signal.anomaly_type.toLowerCase()
  const currentValue = normalizeNumericValue(signal.current_value)
  const baselineValue = normalizeNumericValue(signal.baseline_value)

  if (anomalyType === 'creator_convergence') {
    return signal.description || 'Creator convergence detected: multiple macro/mega creators mentioned this competitor.'
  }

  if (anomalyType === 'paid_creator_push') {
    return signal.description || 'Paid/gifted creator push detected from macro+ creators.'
  }

  if (anomalyType === 'tiktok_shop_livestream') {
    return signal.description || 'TikTok Shop livestream event detected with strong purchase-intent potential.'
  }

  if (anomalyType === 'high_viewership_livestream') {
    return signal.description || 'High-viewership livestream detected.'
  }

  if (channel === 'amazon' && anomalyType.includes('bsr') && currentValue !== null && baselineValue !== null) {
    return `Amazon rank moved from ${formatRank(baselineValue)} to ${formatRank(currentValue)}`
  }

  if (channel === 'tiktok') {
    return 'TikTok activity spike detected'
  }

  if (channel === 'google' || channel === 'google_shopping') {
    return 'New Google Shopping advertiser detected'
  }

  return signal.description || signal.anomaly_type
}

function anomalyTypeBadge(anomalyType: string): { icon: string; label: string } | null {
  const normalized = anomalyType.toLowerCase()
  if (normalized === 'creator_convergence') {
    return { icon: '🧑‍🤝‍🧑', label: 'Creator Convergence' }
  }
  if (normalized === 'paid_creator_push') {
    return { icon: '💸', label: 'Paid Creator Push' }
  }
  if (normalized === 'tiktok_shop_livestream') {
    return { icon: '🛍️', label: 'TikTok Shop Live' }
  }
  if (normalized === 'high_viewership_livestream') {
    return { icon: '📺', label: 'High-Viewership Live' }
  }
  return null
}

function getSignalTimeGroup(value: string | null, nowDate = new Date()): SignalTimeGroup {
  if (!value) {
    return 'Older'
  }

  const detectedAt = new Date(value)
  if (Number.isNaN(detectedAt.getTime())) {
    return 'Older'
  }

  const startOfToday = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate())
  const startOfYesterday = new Date(startOfToday)
  startOfYesterday.setDate(startOfYesterday.getDate() - 1)

  const startOfWeek = new Date(startOfToday)
  const dayOfWeek = startOfWeek.getDay()
  const mondayOffset = (dayOfWeek + 6) % 7
  startOfWeek.setDate(startOfWeek.getDate() - mondayOffset)

  if (detectedAt >= startOfToday) {
    return 'Today'
  }

  if (detectedAt >= startOfYesterday) {
    return 'Yesterday'
  }

  if (detectedAt >= startOfWeek) {
    return 'This Week'
  }

  return 'Older'
}

function normalizeName(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase()
}

function canonicalCompetitorKey(value: string | null | undefined): string {
  return canonicalBrandName(value).toLowerCase()
}

function compareSeverity(a: string | null, b: string | null): number {
  const order: Record<string, number> = { critical: 3, high: 2, medium: 1, low: 0 }
  return (order[(a || 'low').toLowerCase()] || 0) - (order[(b || 'low').toLowerCase()] || 0)
}

function formatHoursAgo(value: string | null | undefined): string | null {
  if (!value) {
    return null
  }
  const parsed = new Date(value).getTime()
  if (!Number.isFinite(parsed)) {
    return null
  }
  const diffHours = Math.round((Date.now() - parsed) / (60 * 60 * 1000))
  if (diffHours <= 24) {
    return null
  }
  return `Last updated ${diffHours}h ago`
}

function formatLastRun(value: string | null | undefined): string {
  if (!value) {
    return 'never'
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return 'unknown'
  }
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function SignalFeedTab({ onNavigateToCompetitor }: SignalFeedTabProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('intelligence')
  const [selectedChannel, setSelectedChannel] = useState('')
  const [selectedSeverity, setSelectedSeverity] = useState('')
  const [selectedCompetitorId, setSelectedCompetitorId] = useState('')
  const [visibleLimit, setVisibleLimit] = useState(RAW_SIGNALS_BATCH_SIZE)
  const [exportError, setExportError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  const { data: signalsData, isLoading, isFetching, error } = useSignals({
    page: 0,
    limit: visibleLimit,
    severity: selectedSeverity || undefined,
    channel: selectedChannel || undefined,
    competitorId: selectedCompetitorId || undefined,
  })
  const {
    data: clustersData,
    isLoading: isClustersLoading,
    error: clustersError,
  } = useSignalClusters({
    severity: selectedSeverity || undefined,
    channel: selectedChannel || undefined,
    competitorId: selectedCompetitorId || undefined,
  })
  const { data: competitorsData } = useCompetitors()
  const { data: actionsResponse } = useActions()

  const pageSignals = useMemo(() => {
    if (!signalsData || !Array.isArray(signalsData.signals)) {
      return []
    }

    return signalsData.signals
  }, [signalsData])

  const clusters = useMemo(() => {
    if (!clustersData || !Array.isArray(clustersData.clusters)) {
      return []
    }
    return clustersData.clusters
  }, [clustersData])

  const mergedClusters = useMemo(() => {
    if (!clusters || clusters.length === 0) return []
    const byBrand = new Map<string, (typeof clusters)[number]>()
    for (const cluster of clusters) {
      const canonicalName = canonicalBrandName(cluster.competitor_name || '')
      const key = canonicalName.toLowerCase().replace(/\s+/g, '')
      const existing = byBrand.get(key)
      if (!existing) {
        byBrand.set(key, { ...cluster, competitor_name: canonicalName || cluster.competitor_name })
      } else {
        const narrativeParts = [existing.narrative, cluster.narrative]
          .map((value) => String(value || '').trim())
          .filter(Boolean)
        const mergedNarrative = Array.from(new Set(narrativeParts)).join('; ')

        byBrand.set(key, {
          ...existing,
          signal_count: (existing.signal_count || 0) + (cluster.signal_count || 0),
          impact_score: Math.max(existing.impact_score || 0, cluster.impact_score || 0),
          max_severity:
            compareSeverity(existing.max_severity, cluster.max_severity) >= 0
              ? existing.max_severity
              : cluster.max_severity,
          narrative: mergedNarrative || existing.narrative,
        })
      }
    }
    return Array.from(byBrand.values()).sort((a, b) => (b.impact_score || 0) - (a.impact_score || 0))
  }, [clusters])

  const competitors = useMemo(() => {
    if (!competitorsData || !Array.isArray(competitorsData.competitors)) {
      return []
    }
    return competitorsData.competitors
  }, [competitorsData])

  const completedActionByCompetitor = useMemo(() => {
    const map = new Map<string, { finding: string | null; action_type: string | null }>()
    const actions = actionsResponse?.actions ?? []

    for (const action of actions) {
      const status = normalizeName(action.status)
      if (status !== 'completed' && status !== 'done') {
        continue
      }
      const key = canonicalCompetitorKey(action.competitor_name)
      if (!key || map.has(key)) {
        continue
      }
      map.set(key, {
        finding: action.finding || action.recommendation || action.action_description || null,
        action_type: action.action_type || null,
      })
    }

    return map
  }, [actionsResponse?.actions])

  const totalSignals = signalsData?.pagination?.total ?? signalsData?.signals_total ?? 0
  const hasMore = pageSignals.length < totalSignals
  const activeSignalCountLabel = totalSignals === 1 ? '1 signal' : `${totalSignals} signals`
  const clusterWorker = clustersData?.worker
  const clusterWorkerStatus = normalizeName(clusterWorker?.status)

  const groupedSignals = useMemo(() => {
    const groups: Array<{ label: SignalTimeGroup; signals: SignalRow[] }> = []

    for (const signal of pageSignals) {
      const label = getSignalTimeGroup(signal.detected_at)
      const lastGroup = groups[groups.length - 1]

      if (!lastGroup || lastGroup.label !== label) {
        groups.push({ label, signals: [signal] })
      } else {
        lastGroup.signals.push(signal)
      }
    }

    return groups
  }, [pageSignals])

  const severityCounts = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 }
    for (const signal of pageSignals) {
      const sev = signal.severity.toLowerCase()
      if (sev === 'critical') counts.critical += 1
      else if (sev === 'high') counts.high += 1
      else if (sev === 'medium') counts.medium += 1
      else counts.low += 1
    }
    return counts
  }, [pageSignals])

  const clusterSeverityCounts = useMemo(() => {
    const counts = { critical: 0, high: 0, medium: 0, low: 0 }
    for (const cluster of mergedClusters) {
      const sev = String(cluster.max_severity || '').toLowerCase()
      if (sev === 'critical') counts.critical += 1
      else if (sev === 'high') counts.high += 1
      else if (sev === 'medium') counts.medium += 1
      else counts.low += 1
    }
    return counts
  }, [mergedClusters])

  useEffect(() => {
    setVisibleLimit(RAW_SIGNALS_BATCH_SIZE)
  }, [selectedChannel, selectedCompetitorId, selectedSeverity])

  const signalsError = error instanceof Error ? error.message : null
  const clustersErrorMessage = clustersError instanceof Error ? clustersError.message : null

  const showingCount = pageSignals.length

  const handleExportCsv = async () => {
    setIsExporting(true)
    setExportError(null)

    try {
      const params = new URLSearchParams()
      params.set('page', '0')
      params.set('limit', '9999')

      if (selectedChannel) {
        params.set('channel', selectedChannel)
      }
      if (selectedSeverity) {
        params.set('severity', selectedSeverity)
      }
      if (selectedCompetitorId) {
        params.set('competitor_id', selectedCompetitorId)
      }

      const payload = await apiClient.get<{ signals: SignalRow[] }>(`/competitive/signals?${params.toString()}`)

      const exportRows = Array.isArray(payload.signals)
        ? payload.signals.map((signal: SignalRow) => ({
            detected_at: signal.detected_at,
            severity: signal.severity,
            channel: signal.signal_channel,
            competitor: signal.competitor_name || 'Global Signal',
            anomaly_type: signal.anomaly_type,
            description: formatSignalDescription(signal),
            z_score: signal.z_score,
          }))
        : []

      exportToCSV(exportRows, `signal-feed-${new Date().toISOString().slice(0, 10)}.csv`)
    } catch (exportCsvError) {
      setExportError(exportCsvError instanceof Error ? exportCsvError.message : 'Failed to export signals')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <SignalIcon className="h-4 w-4 text-slate-500" />
          <h2 className="text-lg font-semibold text-slate-900">Signal Feed</h2>
          <span className="text-sm text-slate-500">({activeSignalCountLabel})</span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedChannel}
            onChange={(event) => setSelectedChannel(event.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <option value="">All channels</option>
            <option value="amazon">Amazon</option>
            <option value="tiktok">TikTok</option>
            <option value="google">Google</option>
          </select>

          <select
            value={selectedSeverity}
            onChange={(event) => setSelectedSeverity(event.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <option value="">All severity</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
          </select>

          <select
            value={selectedCompetitorId}
            onChange={(event) => setSelectedCompetitorId(event.target.value)}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
          >
            <option value="">All competitors</option>
            {competitors.map((competitor) => (
              <option key={competitor.id} value={competitor.id}>
                {competitor.brand_name}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => void handleExportCsv()}
            disabled={isExporting}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setViewMode('intelligence')}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
            viewMode === 'intelligence'
              ? 'bg-[var(--patterniq-navy-900)] text-white'
              : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
          }`}
        >
          📊 Intelligence View
        </button>
        <button
          type="button"
          onClick={() => setViewMode('raw')}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
            viewMode === 'raw'
              ? 'bg-[var(--patterniq-navy-900)] text-white'
              : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
          }`}
        >
          📋 All Signals ({totalSignals})
        </button>
      </div>

      {signalsError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{signalsError}</div>
      )}

      {clustersErrorMessage && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {clustersErrorMessage}
        </div>
      )}

      {exportError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{exportError}</div>
      )}

      {viewMode === 'intelligence' ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
            <div className="flex flex-wrap items-center gap-3">
              <span>{mergedClusters.length} threat stories</span>
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                {clusterSeverityCounts.critical} critical
              </span>
              <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                {clusterSeverityCounts.high} high
              </span>
            </div>
            <button
              type="button"
              onClick={() => setViewMode('raw')}
              className="rounded-md border border-[var(--patterniq-navy-900)] bg-[var(--patterniq-navy-900)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[var(--patterniq-navy-800)]"
            >
              View Raw Signal Feed
            </button>
          </div>

          {isClustersLoading ? (
            <div className="space-y-3">
              <ClusterSkeleton />
              <ClusterSkeleton />
              <ClusterSkeleton />
            </div>
          ) : mergedClusters.length === 0 ? (
            <EmptyState
              icon={<Activity className="h-6 w-6" />}
              title={
                clusterWorkerStatus === 'running'
                  ? 'Signal clustering is running now'
                  : clusterWorker?.has_run
                    ? 'No threat stories generated'
                    : 'Signal clustering not run yet'
              }
              description={
                clusterWorkerStatus === 'running'
                  ? `Signal clustering is currently running. ${totalSignals.toLocaleString('en-US')} raw signals are available in 'All Signals' tab.`
                  : clusterWorker?.has_run
                  ? `${totalSignals.toLocaleString('en-US')} signals collected · 0 threat stories generated. Raw signals are available in the 'All Signals' tab.`
                  : `Signal clustering runs daily at 6:00 AM EST. Last run: ${formatLastRun(clusterWorker?.last_run)}. ${totalSignals.toLocaleString('en-US')} raw signals available in 'All Signals' tab.`
              }
            />
          ) : (
            <div className="space-y-3">
              {mergedClusters.map((cluster) => {
                const staleLabel = formatHoursAgo(cluster.created_at || cluster.cluster_date || null)
                const actionInfo = completedActionByCompetitor.get(canonicalCompetitorKey(cluster.competitor_name))
                return (
                  <ClusterCard
                    key={cluster.id}
                    cluster={cluster}
                    staleLabel={staleLabel}
                    actionTaken={Boolean(actionInfo)}
                    actionFinding={
                      actionInfo?.finding
                        ? humanizeActionFinding(
                            actionInfo.action_type || '',
                            actionInfo.finding,
                            cluster.competitor_name || ''
                          )
                        : null
                    }
                  />
                )
              })}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs text-slate-500">
              Showing {showingCount.toLocaleString('en-US')} of {totalSignals.toLocaleString('en-US')} signals
            </span>
            <div className="flex items-center gap-2">
              {severityCounts.critical > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                  {severityCounts.critical} critical
                </span>
              )}
              {severityCounts.high > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                  {severityCounts.high} high
                </span>
              )}
              {severityCounts.medium > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                  {severityCounts.medium} medium
                </span>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {isLoading && pageSignals.length === 0 ? (
              <SignalRowSkeleton />
            ) : pageSignals.length === 0 ? (
              <EmptyState
                icon={<Activity className="h-6 w-6" />}
                title="No signals yet"
                description="Signals appear when competitors show unusual activity across Amazon, TikTok, or Google."
              />
            ) : (
              groupedSignals.map((group) => (
                <div key={group.label} className="space-y-2">
                  <div className="sticky top-0 z-10 rounded-md bg-slate-100/95 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600 backdrop-blur">
                    {group.label}
                  </div>

                  {group.signals.map((signal) => {
                    const isCritical = signal.severity.toLowerCase() === 'critical'
                    const isHigh = signal.severity.toLowerCase() === 'high'
                    const anomalyBadge = anomalyTypeBadge(signal.anomaly_type)
                    const severityStyle = getSeverityClasses(signal.severity)
                    const canonicalCompetitorName = canonicalBrandName(signal.competitor_name)
                    const competitorDisplayName = canonicalCompetitorName || 'Global Signal'

                    return (
                      <div
                        key={signal.id}
                        className={`rounded-md border p-3 ${
                          isCritical
                            ? 'border-red-200 border-l-4 border-l-red-500 bg-red-50/50 shadow-sm'
                            : isHigh
                              ? 'border-orange-200 border-l-4 border-l-orange-400 bg-orange-50/30'
                              : 'border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span
                              className={`rounded-md border px-2 py-0.5 text-xs font-semibold ${severityStyle.border} ${severityStyle.badge}`}
                            >
                              {signal.severity}
                            </span>
                            <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                              {channelLabel(signal.signal_channel)}
                            </span>
                            {anomalyBadge && (
                              <span className="rounded-md border border-[var(--brand-200)] bg-[var(--brand-50)] px-2 py-0.5 text-xs font-semibold text-[var(--brand-700)]">
                                {anomalyBadge.icon} {anomalyBadge.label}
                              </span>
                            )}
                            {signal.competitor_name && onNavigateToCompetitor ? (
                              <button
                                type="button"
                                onClick={() => onNavigateToCompetitor(competitorDisplayName)}
                                className={`text-sm ${isCritical ? 'font-semibold' : 'font-medium'} text-blue-600 hover:underline`}
                              >
                                {competitorDisplayName}
                              </button>
                            ) : (
                              <span className={`text-sm text-slate-800 ${isCritical ? 'font-semibold' : 'font-medium'}`}>
                                {competitorDisplayName}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">{formatTimestamp(signal.detected_at)}</div>
                        </div>
                        <div className="mt-2 text-sm text-slate-700">{formatSignalDescription(signal)}</div>
                        {signal.z_score !== null && (
                          <div className="mt-2 text-xs text-slate-500">
                            {`${Number(signal.z_score).toFixed(1)}× above normal activity`}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-end gap-2">
            {hasMore ? (
              <button
                type="button"
                disabled={isFetching}
                onClick={() => setVisibleLimit((current) => current + RAW_SIGNALS_BATCH_SIZE)}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
              >
                {isFetching ? 'Loading...' : `Show ${RAW_SIGNALS_BATCH_SIZE} more`}
              </button>
            ) : (
              <span className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700">
                All signals loaded
              </span>
            )}
          </div>
        </>
      )}
    </section>
  )
}
